<template>
  <div class="segment-conditions-form-component" :class="{ 'or-operator': operator === SegmentConditionType.Or }">
    <div class="wrapper" shadow="hover" :class="{ 'child-assertion': isChild }">
      <section v-if="isChild" class="delete-group">
        <vd-button
          v-if="!readOnly && isChild && SegmentConditionType.Or"
          class="delete-group-button"
          type="tertiary"
          size="large"
          @click="onRemoveGroup"
        >
          <vd-custom-icon
            name="close-icon"
            :has-hover-state="conditions.length > 1"
            has-hover-with-parent
            color="var(--vd-color-red)"
            width="20"
            box="0 0 30 30"
          />
        </vd-button>
      </section>

      <div
        v-for="(condition, index) in conditions"
        :key="condition.rowId"
        class="segment-condition"
        :class="{ preview: readOnly }"
      >
        <h3 v-if="index > 0" class="or-separator">{{ operator === SegmentConditionType.Or ? "or" : "and" }}</h3>
        <div v-if="isTestCondition(condition.type)">
          <SegmentFeatureAssertion
            :model-value="condition"
            :features="features"
            :read-only="readOnly"
            :disable-delete-button="conditions.length === 1"
            :error="error && error[condition.rowId]"
            class="feature-assertion"
            @update:model-value="(value) => onAssertionUpdate(condition.rowId, value)"
            @add="(conditionType) => onAssertionAdd(conditionType, condition.rowId)"
            @delete="() => onRemove(condition.rowId)"
          />
        </div>

        <div v-else :key="condition.conditions && condition.conditions.length">
          <SegmentConditionsForm
            :model-value="condition.conditions || []"
            :operator="condition.type"
            :features="features"
            :read-only="readOnly"
            :is-child="conditions.length > 1"
            :error="error"
            @update:model-value="(value) => onConditionUpdate(condition.rowId, value)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from "vue";

import { SegmentConditionType, SegmentFormPageFeatureFragment } from "@/graphql/generated-types";
import { generateRandomId } from "@/utils";

import { Condition, ConditionError } from "../utils";
import SegmentFeatureAssertion from "./SegmentFeatureAssertion.vue";

export default defineComponent({
  name: "SegmentConditionsForm",

  components: {
    SegmentFeatureAssertion,
  },

  props: {
    features: {
      type: Array as PropType<SegmentFormPageFeatureFragment[]>,
      required: true,
    },

    modelValue: {
      type: Array as PropType<Condition[]>,
      required: true,
    },

    operator: {
      type: String as PropType<SegmentConditionType>,
      required: true,
    },

    isChild: {
      type: Boolean,
      default: false,
    },

    /**
     * if true, the form field are disabled
     */
    readOnly: Boolean,

    /**
     * The error structure to pass to the SegmentFeatureAssertion
     */
    error: {
      type: Object as PropType<Record<string, ConditionError>>,
      default: () => null,
    },
  },

  emits: ["update:modelValue"],

  setup(props, { emit }) {
    const conditions = ref<Condition[]>(props.modelValue);

    const onAssertionUpdate = (rowId: string, value: Condition) => {
      conditions.value = conditions.value.map((condition) =>
        condition.rowId === rowId ? { ...condition, ...value } : condition
      );
      emit("update:modelValue", conditions.value);
    };

    const onRemove = (rowId: string) => {
      conditions.value = conditions.value.filter((o) => o.rowId !== rowId);
      emit("update:modelValue", conditions.value);
    };

    const onRemoveGroup = () => {
      emit("update:modelValue", []);
    };

    const onConditionUpdate = (rowId: string, value: Condition[]) => {
      conditions.value = conditions.value.map((condition) =>
        condition.rowId === rowId ? { ...condition, conditions: value } : condition
      );
      emit("update:modelValue", conditions.value);
    };

    const isTestCondition = (type: SegmentConditionType) => type === SegmentConditionType.Test;

    const onAssertionAdd = (conditionType: SegmentConditionType, rowId: string) => {
      const selectedConditionIndex = conditions.value.findIndex((o) => o.rowId === rowId);
      if (selectedConditionIndex === -1) {
        return;
      }
      const testCondition = {
        type: SegmentConditionType.Test,
        featureId: null,
        operator: null,
        testValue: null,
        conditions: [],
        rowId: generateRandomId(),
      };

      let inner = [conditions.value[selectedConditionIndex], testCondition];

      inner =
        props.operator === conditionType
          ? inner
          : [
              {
                type: conditionType,
                rowId: generateRandomId(),
                conditions: inner,
              },
            ];

      conditions.value = [
        ...conditions.value.slice(0, selectedConditionIndex),
        ...inner,
        ...conditions.value.slice(selectedConditionIndex + 1),
      ];
      emit("update:modelValue", conditions.value);
    };

    return {
      SegmentConditionType,
      conditions,
      isTestCondition,
      onAssertionAdd,
      onAssertionUpdate,
      onRemove,
      onRemoveGroup,
      onConditionUpdate,
    };
  },
});
</script>

<style lang="scss">
body > .segment-conditions-form-component > .delete-group-button:not(:first-child) {
  display: none;
}

.segment-conditions-form-component {
  margin: size("XS") 0;
  width: 100%;

  .wrapper {
    &.child-assertion {
      border-left: 2px solid $foggy-grey;
      padding: size("ML");
      border: 1px dotted var(--voodootune-accent-color);
      border-radius: 5px;

      &:hover {
        box-shadow: 0 0 11px rgba(33, 33, 33, 0.2);

        .delete-group-button {
          visibility: visible;
        }
      }
    }

    .delete-group {
      margin-bottom: size("M");
      display: flex;
      justify-content: flex-end;
      position: relative;
      top: -35px;
      right: -25px;
      height: 0;

      .delete-group-button {
        visibility: hidden;

        &:hover {
          .secondary-color {
            fill: $red-alert;
          }
        }

        .secondary-color {
          fill: $red-ion;
        }
      }

      h3 {
        color: $intense-blue;
      }

      h3 {
        line-height: inherit;
      }
    }

    .or-separator {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      line-height: size("XL");
      margin-bottom: size("M");
      font-variant: small-caps;
      color: var(--voodootune-accent-color);
      font-weight: 600;
    }

    .delete-group-button {
      width: size("ML");
      height: size("ML");

      .el-button {
        padding: 3px;
      }
    }
  }

  .dropdown-link {
    color: $intense-blue;
  }
}
</style>
